/**
 * Provides a configurable accessibility settings panel that allows users
 * to customize accessibility preferences such as text-to-speech, display,
 * navigation, visual enhancements, and interface scaling across the application.
 */
import { ChevronLeft } from "@untitledui/icons";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Drawer } from "@/Components/Drawer";
import { useAccessibilitySettings } from "@/hooks/useAccessibilitySettings";
import { useDeviceType } from "@/hooks/useDeviceType";
import type { AccessibilitySettings } from "@/lib/accessibility";
import { clamp2, DEFAULTS, TTSRuntime } from "@/lib/accessibility";
import { selectCommonConfig } from "@/store/adminSlice";

type Props = {
  open: boolean;
  onClose: () => void;
};

function AccessibilityPanel({ open, onClose }: Props) {
  const { t } = useTranslation();
  const { settings, setSettings, saveSettings } = useAccessibilitySettings();
  const { isMobile, isTablet } = useDeviceType();
  const isTouchDevice = isMobile || isTablet;

  const [draft, setDraft] = React.useState<AccessibilitySettings>(settings);

  const titleId = "a11y-title";
  const pct = Math.round(draft.fontScale * 100);

  // Get max zoom level based on device type
  const getMaxZoom = () => {
    if (isMobile) return 110;
    if (isTablet) return 120;
    return 150;
  };

  const maxZoom = getMaxZoom();

  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  // Clamp zoom when device type changes
  React.useEffect(() => {
    const maxZoomPercent = maxZoom;
    if (pct > maxZoomPercent) {
      set("fontScale", maxZoomPercent / 100);
    }
  }, [isMobile, isTablet]);

  const resetAll = async () => {
    setDraft(DEFAULTS);
    setSettings(DEFAULTS);
    await saveSettings(DEFAULTS);
  };

  const handleClose = () => {
    document.documentElement.classList.remove("overflow-hidden");
    setDraft(settings);
    onClose();
  };

  React.useEffect(() => {
    if (!open) return;

    document.documentElement.classList.add("overflow-hidden");

    return () => {
      document.documentElement.classList.remove("overflow-hidden");
    };
  }, [open]);

  const applyAndClose = async () => {
    try {
      setSettings(draft);
      await saveSettings(draft);
    } finally {
      document.documentElement.classList.remove("overflow-hidden");
      document.body.classList.remove("overflow-hidden");

      onClose();
    }
  };

  React.useEffect(() => {
    if (settings.ttsEnabled) {
      TTSRuntime.setRate(settings.speechRate);
      TTSRuntime.speak(t("components.accessibilityPanel.speech.enabledAnnouncement"));
      TTSRuntime.readPage();
    } else {
      TTSRuntime.stop();
    }
  }, [settings.ttsEnabled, settings.speechRate, t]);

  React.useEffect(() => {
    if (open) {
      setDraft(settings);
    }
  }, [open, settings]);

  const config = useSelector(selectCommonConfig);
  const Content = (
    <>
      <div className="space-y-6">
        <div className="">
          {config?.CursorSize && !isTouchDevice && (
            <Section
              title={t("components.accessibilityPanel.sections.cursorSize.title")}
              description={t("components.accessibilityPanel.sections.cursorSize.description")}
            >
              <Segmented
                options={[
                  {
                    label: t("components.accessibilityPanel.cursorOptions.normal"),
                    value: "normal",
                  },
                  {
                    label: t("components.accessibilityPanel.cursorOptions.large"),
                    value: "large",
                  },
                  {
                    label: t("components.accessibilityPanel.cursorOptions.xlarge"),
                    value: "xlarge",
                  },
                ]}
                value={draft.cursorSize}
                onChange={(v: any) => set("cursorSize", v)}
              />
            </Section>
          )}
        </div>

        {!isTouchDevice && (
          <Group
            title={t("components.accessibilityPanel.groups.voiceSpeech.title")}
            description={t("components.accessibilityPanel.groups.voiceSpeech.description")}
          >
            {config?.TTS && (
              <Toggle
                label={t("components.accessibilityPanel.toggles.textToSpeech.label")}
                description={t("components.accessibilityPanel.toggles.textToSpeech.description")}
                checked={draft.ttsEnabled}
                onChange={(v) => set("ttsEnabled", v)}
              />
            )}

            {config?.SpeechRate && draft.ttsEnabled && (
              <div className="p-3">
                <label className="text-sm font-medium text-[var(--text-primary)]">
                  {t("components.accessibilityPanel.speech.speechRate")}
                </label>

                <div className="flex items-center gap-3 w-full flex-nowrap">
                  <input
                    type="range"
                    min={0.5}
                    max={2}
                    step={0.1}
                    value={draft.speechRate}
                    onChange={(e) => set("speechRate", parseFloat(e.target.value))}
                    className="
          flex-1 min-w-0
          h-2.5 rounded-full appearance-none cursor-pointer

          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:h-4
          [&::-webkit-slider-thumb]:w-4
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:bg-white
          [&::-webkit-slider-thumb]:border
          [&::-webkit-slider-thumb]:border-[var(--color-brand)]
          [&::-webkit-slider-thumb]:shadow

          [&::-moz-range-thumb]:h-4
          [&::-moz-range-thumb]:w-4
          [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:bg-white
          [&::-moz-range-thumb]:border
          [&::-moz-range-thumb]:border-[var(--color-brand)]
        "
                    style={{
                      background: (() => {
                        const percent = ((draft.speechRate - 0.5) / (2 - 0.5)) * 100;
                        return `linear-gradient(to right,var(--color-brand) 0%,var(--color-brand) ${percent}%,#e5e7eb ${percent}%,#e5e7eb 100%)`;
                      })(),
                    }}
                  />

                  <span className="text-sm text-gray-600 shrink-0">
                    {Math.round(draft.speechRate * 100)}%
                  </span>
                </div>
              </div>
            )}

            <div className="">
              {config?.TTSOnFocus && (
                <Toggle
                  label={t("components.accessibilityPanel.toggles.readOnFocus.label")}
                  description={t("components.accessibilityPanel.toggles.readOnFocus.description")}
                  checked={draft.ttsOnFocus}
                  onChange={(v) => set("ttsOnFocus", v)}
                />
              )}

              {config?.TTSOnHover && (
                <Toggle
                  label={t("components.accessibilityPanel.toggles.readOnHover.label")}
                  description={t("components.accessibilityPanel.toggles.readOnHover.description")}
                  checked={draft.ttsOnHover}
                  onChange={(v) => set("ttsOnHover", v)}
                />
              )}
            </div>
          </Group>
        )}

        <Group
          title={t("components.accessibilityPanel.groups.screenReaderCognitive.title")}
          description={t("components.accessibilityPanel.groups.screenReaderCognitive.description")}
        >
          <div className="">
            {config?.ScreenReaderOptimization && !isTouchDevice && (
              <Toggle
                label={t("components.accessibilityPanel.toggles.screenReaderOptimization.label")}
                description={t(
                  "components.accessibilityPanel.toggles.screenReaderOptimization.description"
                )}
                checked={draft.screenReaderOptimization}
                onChange={(v) => set("screenReaderOptimization", v)}
              />
            )}
          </div>

          {config?.DyslexiaFriendlyFont && (
            <Toggle
              label={t("components.accessibilityPanel.toggles.dyslexiaFriendlyFont.label")}
              description={t(
                "components.accessibilityPanel.toggles.dyslexiaFriendlyFont.description"
              )}
              checked={draft.dyslexiaFriendlyFont}
              onChange={(v) => set("dyslexiaFriendlyFont", v)}
            />
          )}
        </Group>

        <Section
          title={t("components.accessibilityPanel.sections.interfaceScaling.title")}
          description={t("components.accessibilityPanel.sections.interfaceScaling.description")}
        >
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-900">
              {t("components.accessibilityPanel.sections.interfaceScaling.zoomLabel")}
            </label>
          </div>

          <div className="flex items-center gap-3 w-full flex-nowrap">
            <input
              type="range"
              min={75}
              max={maxZoom}
              step={5}
              value={Math.min(pct, maxZoom)}
              onChange={(e) => set("fontScale", clamp2(Number(e.target.value) / 100))}
              className="
          flex-1 min-w-0
          h-2.5 rounded-full appearance-none cursor-pointer

          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:h-4
          [&::-webkit-slider-thumb]:w-4
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:bg-white
          [&::-webkit-slider-thumb]:border
          [&::-webkit-slider-thumb]:border-[var(--color-brand)]
          [&::-webkit-slider-thumb]:shadow

          [&::-moz-range-thumb]:h-4
          [&::-moz-range-thumb]:w-4
          [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:bg-white
          [&::-moz-range-thumb]:border
          [&::-moz-range-thumb]:border-[var(--color-brand)]
        "
              style={{
                background: `linear-gradient(to right,var(--color-brand) 0%,var(--color-brand) ${
                  ((Math.min(pct, maxZoom) - 75) / (maxZoom - 75)) * 100
                }%,#e5e7eb ${((Math.min(pct, maxZoom) - 75) / (maxZoom - 75)) * 100}%,#e5e7eb 100%)`,
              }}
            />
            <span className="text-sm text-gray-600 shrink-0">{pct}%</span>
          </div>
        </Section>

        <Group
          title={t("components.accessibilityPanel.groups.visualEnhancements.title")}
          description={t("components.accessibilityPanel.groups.visualEnhancements.description")}
        >
          {config?.HighContrast && (
            <Toggle
              label={t("components.accessibilityPanel.toggles.highContrast.label")}
              description={t("components.accessibilityPanel.toggles.highContrast.description")}
              checked={draft.highContrast}
              onChange={(v) => set("highContrast", v)}
            />
          )}

          {config?.UnderlineLinks && (
            <Toggle
              label={t("components.accessibilityPanel.toggles.underlineLinks.label")}
              description={t("components.accessibilityPanel.toggles.underlineLinks.description")}
              checked={draft.underlineLinks}
              onChange={(v) => set("underlineLinks", v)}
            />
          )}

          {config?.TextSpacing && (
            <Toggle
              label={t("components.accessibilityPanel.toggles.increaseTextSpacing.label")}
              description={t(
                "components.accessibilityPanel.toggles.increaseTextSpacing.description"
              )}
              checked={draft.increasedTextSpacing}
              onChange={(v) => set("increasedTextSpacing", v)}
            />
          )}
        </Group>

        <div className="">
          {config?.ReduceMotion && !isTouchDevice && (
            <Group
              title={t("components.accessibilityPanel.groups.motionAnimation.title")}
              description={t("components.accessibilityPanel.groups.motionAnimation.description")}
            >
              <Toggle
                label={t("components.accessibilityPanel.toggles.reduceMotion.label")}
                description={t("components.accessibilityPanel.toggles.reduceMotion.description")}
                checked={draft.reduceMotion}
                onChange={(v) => set("reduceMotion", v)}
              />
            </Group>
          )}
        </div>

        <div className="">
          {!isTouchDevice && (config?.KeyboardNavigation || config?.FocusIndicators) && (
            <Group
              title={t("components.accessibilityPanel.groups.keyboardNavigation.title")}
              description={t("components.accessibilityPanel.groups.keyboardNavigation.description")}
            >
              {config?.KeyboardNavigation && (
                <Toggle
                  label={t("components.accessibilityPanel.toggles.enhancedKeyboardNav.label")}
                  description={t(
                    "components.accessibilityPanel.toggles.enhancedKeyboardNav.description"
                  )}
                  checked={draft.enhancedKeyboardNav}
                  onChange={(v) => set("enhancedKeyboardNav", v)}
                />
              )}

              {config?.FocusIndicators && (
                <Toggle
                  label={t("components.accessibilityPanel.toggles.enhancedFocusIndicators.label")}
                  description={t(
                    "components.accessibilityPanel.toggles.enhancedFocusIndicators.description"
                  )}
                  checked={draft.enhancedFocusIndicators}
                  onChange={(v) => set("enhancedFocusIndicators", v)}
                />
              )}
            </Group>
          )}
        </div>
      </div>
    </>
  );

  if (isMobile) {
    if (!open) return null;

    return (
      <div className="fixed inset-0 z-[1200] flex flex-col bg-[var(--bg-main)]">
        {/* Top Bar */}
        <div className="flex items-center gap-3 px-4 py-2 bg-white border-b">
          <button onClick={handleClose}>
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div>
            <h1 className="text-lg font-semibold">
              {t("components.accessibilityPanel.header.title")}
            </h1>
            <p className="text-xs text-gray-500">
              {t("components.accessibilityPanel.header.mobdescription")}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 pb-24 space-y-6">{Content}</div>

        {/* Bottom Buttons */}
        <div className="sticky bottom-0 z-10 px-4 py-3 bg-white border-t flex justify-end gap-3">
          <button
            onClick={resetAll}
            className="px-4 py-1.5 rounded-md text-sm font-medium bg-gray-100"
          >
            {t("components.accessibilityPanel.buttons.resetAll")}
          </button>

          <button
            onClick={applyAndClose}
            className="px-4 py-1.5 rounded-md text-sm font-medium bg-[var(--color-brand)] text-white"
          >
            {t("components.accessibilityPanel.buttons.applySettings")}
          </button>
        </div>
      </div>
    );
  } else {
    if (!open) return null;

    return (
      <Drawer
        open={open}
        onClose={() => {
          setDraft(settings);
          onClose();
        }}
        titleId={titleId}
      >
        <div className="flex flex-col h-full bg-[var(--panel-bg)] transition-colors duration-500">
          <header className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
            <div>
              <h2 id={titleId} className="text-lg font-semibold">
                {t("components.accessibilityPanel.header.title")}
              </h2>
              <p className="text-sm text-[var(--text-primary)] opacity-70">
                {t("components.accessibilityPanel.header.description")}
              </p>
            </div>

            <button
              onClick={handleClose}
              aria-label={t("components.accessibilityPanel.aria.close")}
              className="p-2 rounded-md hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            >
              ✕
            </button>
          </header>

          <div className="h-[calc(100%-96px-72px)] overflow-y-auto px-5 py-4 space-y-6">
            {Content}
          </div>

          <footer className="sticky bottom-0 px-5 py-3 bg-[var(--bg-surface)] border-t border-[var(--border-color)]">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={resetAll}
                className="px-4 py-1.5 rounded-md text-sm font-medium bg-[var(--select-bg)] text-[var(--text-primary)] hover:opacity-90 transition"
              >
                {t("components.accessibilityPanel.buttons.resetAll")}
              </button>

              <button
                onClick={applyAndClose}
                className="px-4 py-1.5 rounded-md text-sm font-medium bg-[var(--color-brand)] text-white hover:opacity-90 transition"
              >
                {t("components.accessibilityPanel.buttons.applySettings")}
              </button>
            </div>
          </footer>
        </div>
      </Drawer>
    );
  }
}

/* ---------- Local UI primitives ---------- */

function Section(props: { title: string; description?: string; children: React.ReactNode }) {
  const { isMobile } = useDeviceType();
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">{props.title}</h3>
        {props.description && (
          <p className="text-sm text-[var(--text-primary)] opacity-70">{props.description}</p>
        )}
      </div>

      <div
        className={`p-3 rounded-lg ${
          isMobile && props.description?.includes("zoom") // detect zoom section
            ? ""
            : "bg-[var(--panel-bg)]"
        }`}
      >
        {props.children}
      </div>
    </section>
  );
}

function Group(props: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <div>
        <h3 className="text-sm font-semibold">{props.title}</h3>
        {props.description && (
          <p className="text-sm text-[var(--text-primary)] opacity-70">{props.description}</p>
        )}
      </div>

      <div className="rounded-lg overflow-hidden">{props.children}</div>
    </section>
  );
}

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = React.useId();
  const labelId = `${id}-label`;
  const descId = `${id}-desc`;

  return (
    <div
      className="flex items-center justify-between gap-4 p-3"
      aria-label={`${label}. ${description ?? ""}`} // FIX (important)
    >
      <label htmlFor={id} className="text-sm">
        <div id={labelId} className="font-medium" aria-label={label}>
          {label}
        </div>
        {description && (
          <div id={descId} className="text-slate-600" aria-label={description}>
            {description}
          </div>
        )}
      </label>

      <button
        id={id}
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={description ? descId : undefined}
        onClick={() => onChange(!checked)}
        className={`
          relative inline-flex h-[24px] w-[44px]
          shrink-0
          rounded-full transition-colors
          mt-[2px]
          ${checked ? "bg-[var(--color-brand)]" : "bg-gray-300"}
        `}
      >
        <span
          className={`
            absolute top-1/2 -translate-y-1/2
            h-[18px] w-[18px] rounded-full
            bg-[rgb(246,244,239)]
            transition-all duration-200
            ${checked ? "right-[3px]" : "left-[3px]"}
          `}
        />
      </button>
    </div>
  );
}

function Segmented({ options, value, onChange }: any) {
  return (
    <div className="inline-flex rounded-md bg-[var(--select-bg)] overflow-hidden">
      {options.map((opt: any) => {
        const active = value === opt.value;

        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`
              px-3 py-1.5 text-sm font-medium transition
              ${
                active
                  ? "bg-[var(--color-brand)] text-white"
                  : "bg-[var(--select-bg)] text-[var(--text-primary)] hover:opacity-90"
              }
            `}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
export default AccessibilityPanel;
